/**
 * WP-V2-TW — gracz: AnimatedDogAvatar + WalkController (tap/WASD + colliders).
 * Pozycję egzekwuje WalkController; avatar = animacja / strój / hold.
 * Strój z OutfitStore (nie z registry — nikt nie robi registry.set('dogOutfit')).
 */
import Phaser from 'phaser';
import { AnimatedDogAvatar } from '../../avatar/AnimatedDogAvatar.js';
import { OutfitStore } from '../../avatar/outfitStore.js';
import { emptyOutfitSlots } from '../../avatar/parseOutfit.js';
import { WalkController } from '../../core/world/WalkController.js';
import type { ColliderRect, WalkableTarget } from '../../core/world/types.js';
import { TOWN_BOUNDS, TOWN_PLAYER_START } from './hotspots.js';
import { depthForGroundY } from './townLayout.js';

export class PlayerPawn implements WalkableTarget {
  readonly avatar: AnimatedDogAvatar;
  readonly container: Phaser.GameObjects.Container;
  private walk: WalkController;
  private marker: Phaser.GameObjects.Arc | null = null;
  private label: Phaser.GameObjects.Text;

  constructor(
    private readonly scene: Phaser.Scene,
    colliders: readonly ColliderRect[],
  ) {
    const cache = OutfitStore.load();
    const breed =
      cache.breed || (scene.registry.get('dogBreed') as string) || 'maltese';
    const color =
      cache.color || (scene.registry.get('dogColor') as string) || undefined;
    const outfit = cache.slots ?? emptyOutfitSlots();

    this.avatar = new AnimatedDogAvatar({
      scene,
      x: TOWN_PLAYER_START.x,
      y: TOWN_PLAYER_START.y,
      breed,
      color,
      scale: 0.95,
      slots: outfit,
      depth: depthForGroundY(TOWN_PLAYER_START.y),
    });
    this.container = this.avatar.container;

    this.label = scene.add
      .text(0, 52, 'Ty', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#4a3f55',
        fontStyle: 'bold',
        backgroundColor: '#ffffffaa',
        padding: { x: 6, y: 2 },
      })
      .setOrigin(0.5);
    this.container.add(this.label);

    this.walk = new WalkController(scene, this, {
      colliders: [...colliders],
      bounds: { ...TOWN_BOUNDS },
      enableInput: true,
    });

    // Powrót z Wardrobe/Shop (wake/resume) — odśwież strój bez restartu gry.
    scene.events.on('wake', this.refreshOutfit, this);
    scene.events.on('resume', this.refreshOutfit, this);
  }

  /** Odczytaj aktualny strój z OutfitStore i nałóż na awatar. */
  refreshOutfit(): void {
    const cache = OutfitStore.load();
    const slots = cache.slots ?? emptyOutfitSlots();
    this.avatar.setOutfit(slots, cache.swimSlots);
    if (cache.breed || cache.color) {
      this.avatar.setBreed(cache.breed || 'maltese', cache.color);
    }
  }

  moveTo(x: number, y: number): void {
    this.avatar.moveTo(x, y);
  }

  setVelocity(vx: number, vy: number): void {
    this.avatar.setVelocity(vx, vy);
  }

  /** Programowy cel (np. wejście do budynku). */
  setTarget(x: number, y: number): void {
    this.walk.moveTo(x, y);
    this.showMarker(x, y);
  }

  get position(): { x: number; y: number } {
    return this.walk.position;
  }

  get isMoving(): boolean {
    return this.walk.isMoving || this.avatar.isMoving;
  }

  /** InteractTarget — pozycja dla ObjectLayer. */
  getPosition(): { x: number; y: number } {
    return { x: this.container.x, y: this.container.y };
  }

  get x(): number {
    return this.container.x;
  }
  get y(): number {
    return this.container.y;
  }

  holdItem(textureKey: string | null): void {
    this.avatar.holdItem(textureKey);
  }

  playAction(
    a: 'sit' | 'jump' | 'wave' | 'eat' | 'swim' | 'sleep' | 'happy' | 'sniff' | string,
  ): Promise<void> {
    const action =
      a === 'sniff' ? 'happy' : (a as 'sit' | 'jump' | 'wave' | 'eat' | 'swim' | 'sleep' | 'happy');
    return this.avatar.playAction(action);
  }

  private showMarker(x: number, y: number): void {
    this.marker?.destroy();
    this.marker = this.scene.add
      .circle(x, y, 12, 0xffffff, 0.35)
      .setStrokeStyle(2, 0xffb6c1)
      .setDepth(5);
    this.scene.tweens.add({
      targets: this.marker,
      scaleX: 1.4,
      scaleY: 1.4,
      alpha: 0,
      duration: 500,
      onComplete: () => {
        this.marker?.destroy();
        this.marker = null;
      },
    });
  }

  update(dtSec: number): void {
    this.walk.update(dtSec);
    // WalkController ustawia pozycję + velocity; avatar animuje.
    // Korekta: po avatar.update przywróć pozycję z walk (uniknij podwójnego ruchu).
    const p = this.walk.position;
    this.avatar.update(dtSec);
    this.container.setPosition(p.x, p.y);
    // W9-A: z-sort jak budynki / NPC / koty
    this.container.setDepth(depthForGroundY(p.y));
  }

  destroy(): void {
    this.scene.events.off('wake', this.refreshOutfit, this);
    this.scene.events.off('resume', this.refreshOutfit, this);
    this.walk.destroy();
    this.marker?.destroy();
    this.label.destroy();
    this.avatar.destroy();
  }
}
